/**
 * Security utilities for input sanitization and XSS prevention
 */

/**
 * Sanitizes user input to prevent XSS attacks
 * @param input - The user input string to sanitize
 * @returns Sanitized string safe for display
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';
  
  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/script/gi, '') // Remove script tags
    .replace(/data:/gi, '') // Remove data: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .replace(/expression\s*\(/gi, '') // Remove CSS expressions
    .replace(/url\s*\(/gi, '') // Remove CSS url functions
    .trim()
    .substring(0, 255); // Limit length
}

/**
 * Escapes HTML special characters for safe display
 * @param text - Text to escape
 * @returns HTML-escaped text
 */
export function escapeHtml(text: string): string {
  if (!text) return '';
  
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Validates file name for security
 * @param fileName - File name to validate
 * @returns Sanitized file name
 */
export function sanitizeFileName(fileName: string): string {
  if (!fileName) return 'unnamed';
  
  return fileName
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid characters
    .replace(/\.\./g, '') // Remove path traversal attempts
    .replace(/^\.+/, '') // Remove leading dots
    .replace(/[^\x20-\x7E]/g, '') // Remove non-ASCII characters
    .trim()
    .substring(0, 100); // Limit length
}

/**
 * Validates data name input
 * @param dataName - Data name to validate
 * @returns Sanitized data name
 */
export function sanitizeDataName(dataName: string): string {
  if (!dataName) return '';
  
  return dataName
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid characters
    .replace(/\.\./g, '') // Remove path traversal attempts
    .replace(/[^\x20-\x7E]/g, '') // Remove non-ASCII characters
    .trim()
    .substring(0, 50); // Limit length
}

/**
 * Checks if input contains potentially malicious content
 * @param input - Input to check
 * @returns true if input appears safe
 */
export function isInputSafe(input: string): boolean {
  if (!input) return true;
  
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<link/i,
    /<meta/i,
    /<style/i,
    /expression\s*\(/i,
    /url\s*\(/i,
    /data:/i,
    /vbscript:/i,
    /<form/i,
    /<input/i,
    /<textarea/i,
    /<select/i,
    /<option/i,
    /<button/i,
    /<a\s+href/i,
    /<img\s+src/i,
    /<svg/i,
    /<math/i,
    /<iframe/i,
    /<frame/i,
    /<frameset/i,
    /<applet/i,
    /<base/i,
    /<bgsound/i,
    /<body/i,
    /<canvas/i,
    /<command/i,
    /<details/i,
    /<dialog/i,
    /<fieldset/i,
    /<legend/i,
    /<marquee/i,
    /<menu/i,
    /<menuitem/i,
    /<meter/i,
    /<noscript/i,
    /<optgroup/i,
    /<output/i,
    /<progress/i,
    /<source/i,
    /<track/i,
    /<video/i,
    /<audio/i,
    /<picture/i,
    /<template/i,
    /<time/i,
    /<wbr/i
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(input));
}

/**
 * Validates file type for security
 * @param file - File object to validate
 * @returns true if file type is safe
 */
export function isFileTypeSafe(file: File): boolean {
  if (!file) return false;
  
  // Allow .exe files (check by extension)
  const fileName = file.name.toLowerCase();
  const allowedExecutables = ['.exe'];
  if (allowedExecutables.some(ext => fileName.endsWith(ext))) {
    return true; // Allow .exe files
  }
  
  // Block other executable files
  const dangerousTypes = [
    'application/x-executable',
    'application/x-msdos-program',
    'application/x-winexe',
    'application/x-msi',
    'application/x-apple-diskimage',
    'application/x-debian-package',
    'application/x-redhat-package-manager',
    'application/x-rpm',
    'application/x-sh',
    'application/x-shellscript',
    'text/x-shellscript',
    'application/x-bat',
    'application/x-msdos-batch',
    'text/x-bat',
    'text/x-msdos-batch'
  ];
  
  // Note: application/x-msdownload is removed from dangerous types to allow .exe files
  return !dangerousTypes.some(type => file.type.includes(type));
}

/**
 * Validates file size
 * @param file - File object to validate
 * @param maxSize - Maximum size in bytes (default: 100GB)
 * @returns true if file size is within limits
 */
export function isFileSizeValid(file: File, maxSize: number = 100 * 1024 * 1024 * 1024): boolean {
  if (!file) return false;
  return file.size > 0 && file.size <= maxSize;
}
